import { openOfflineDb, OFFLINE_KEY_STORE } from './offlineDb';
import { decryptVaultPayload, encryptVaultPayload, getVaultStatus } from './vault';

const KEY_STORE = OFFLINE_KEY_STORE;
const KEY_ID = "deviceKey";

async function getKeyRecord() {
  const db = await openOfflineDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(KEY_STORE, "readonly");
    const store = tx.objectStore(KEY_STORE);
    const req = store.get(KEY_ID);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

export async function getStoredDeviceKeyRecord() {
  return getKeyRecord();
}

async function getOrInsertKeyRecord(record) {
  const db = await openOfflineDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(KEY_STORE, "readwrite");
    const store = tx.objectStore(KEY_STORE);
    let selected;
    // IndexedDB serializes read/write transactions across tabs. Recheck inside
    // this transaction so concurrent first saves all use the winning key.
    const request = store.get(KEY_ID);
    request.onsuccess = () => {
      selected = request.result || record;
      if (!request.result) store.add(record);
    };
    tx.oncomplete = () => { db.close(); resolve(selected); };
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => { db.close(); reject(tx.error || new Error('Device key storage failed.')); };
  });
}

function bytesToBase64(bytes) {
  let binary = "";
  const arr = new Uint8Array(bytes);
  for (let i = 0; i < arr.byteLength; i++) {
    binary += String.fromCharCode(arr[i]);
  }
  return btoa(binary);
}

function base64ToBytes(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export async function getOrCreateKey({ create = true } = {}) {
  // Try to read existing exported key from IndexedDB
  const rec = await getKeyRecord();
  if (rec && rec.key) {
    const raw = base64ToBytes(rec.key);
    return await crypto.subtle.importKey(
      "raw",
      raw,
      { name: "AES-GCM" },
      true,
      ["encrypt", "decrypt"]
    );
  }

  if (!create) throw new Error('The original device key is missing. Keep this browser data for recovery.');

  // Create new key and store export
  const key = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
  const raw = await crypto.subtle.exportKey("raw", key);
  const b64 = bytesToBase64(raw);
  const selected = await getOrInsertKeyRecord({ id: KEY_ID, key: b64 });
  return crypto.subtle.importKey('raw', base64ToBytes(selected.key),
    { name: 'AES-GCM' }, true, ['encrypt', 'decrypt']);
}

export async function encryptPayload(payload) {
  const vault = await getVaultStatus();
  if (vault.enrolled) return encryptVaultPayload(payload);
  const key = await getOrCreateKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = new TextEncoder().encode(JSON.stringify(payload));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, data);
  return {
    iv: bytesToBase64(iv),
    data: bytesToBase64(ct),
  };
}

export async function decryptPayload(record) {
  if (record?.version === 2) return decryptVaultPayload(record);
  if (!record || !record.iv || !record.data) return null;
  const key = await getOrCreateKey({ create: false });
  const iv = base64ToBytes(record.iv);
  const ct = base64ToBytes(record.data);
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ct);
  const json = new TextDecoder().decode(pt);
  return JSON.parse(json);
}
